import {useCallback} from 'react';

import {ToolCallStatus} from 'llm/chat-library/progress/ProgressTypes';
import {
  ToolType,
  TOOL_DISPLAY_NAMES_IN_PROGRESS,
  TOOL_DISPLAY_NAMES_COMPLETED,
  isMCPTool,
  getBasicToolDisplayName,
  enrichDisplayName,
} from 'llm/chat-library/tool-details/types';
import {useMCPToolMappingOptional} from 'llm/mcp-client-library/MCPToolMappingContext';

/**
 * React hook for getting user-friendly tool display names.
 *
 * For MCP tools, returns friendly names like "Server Name: Tool Title"
 * instead of the raw "serverApiId__toolName" format.
 *
 * For built-in tools, returns the configured description from the
 * TOOL_DISPLAY_NAMES_IN_PROGRESS or TOOL_DISPLAY_NAMES_COMPLETED map
 * depending on the optional status parameter. Defaults to in-progress names.
 * For LoadSkill, appends the skill name from args when available
 * (e.g. "Loading skill: My Skill" or "Used Skill: My Skill").
 */
export function useToolDisplayName(): (
  toolName: ToolType | string,
  args?: Record<string, unknown>,
  status?: ToolCallStatus
) => string {
  const mcpMapping = useMCPToolMappingOptional();

  return useCallback(
    (toolName: ToolType | string, args?: Record<string, unknown>, status?: ToolCallStatus): string => {
      if (isMCPTool(toolName)) {
        return mcpMapping?.getFullToolDisplayName(toolName) ?? getBasicToolDisplayName(toolName);
      }

      const descriptions =
        status === ToolCallStatus.COMPLETED ? TOOL_DISPLAY_NAMES_COMPLETED : TOOL_DISPLAY_NAMES_IN_PROGRESS;
      if (descriptions.hasOwnProperty(toolName)) {
        const description = descriptions[toolName as ToolType] ?? toolName;
        return enrichDisplayName(toolName, description, args);
      }

      return getBasicToolDisplayName(toolName, args);
    },
    [mcpMapping]
  );
}
